import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart2,
  DollarSign,
  LucideIcon,
  Minus,
  Package,
  PieChart,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  aggregatesService,
  getDashboardTimezone,
  ProductAggregateItem,
  ProviderAggregateItem,
} from "../../services/aggregatesService";
import { basketsV3Service } from "../../services/basketsV3Service";
import { portfoliosV3Service } from "../../services/portfoliosV3Service";
import {
  BasketListItem,
  PaginatedResponse,
  PortfolioListItem,
} from "../../types/productV3";
import {
  formatCurrencyWithCommas,
  formatNumber,
  formatPercentage,
} from "../../utils/numberUtils";
import {
  useD3LineChart,
  type ChartDataPoint,
  type LineConfig,
  type XAxisTickConfig,
} from "../../hooks/useD3LineChart";

const PAGE_SIZE = 200;

interface ChartColorToken {
  stroke: string;
  swatchClassName: string;
}

const PRODUCT_CHART_COLORS: ChartColorToken[] = [
  { stroke: "var(--rp-chart-1)", swatchClassName: "bg-rp-chart-1" },
  { stroke: "var(--rp-chart-2)", swatchClassName: "bg-rp-chart-2" },
  { stroke: "var(--rp-chart-3)", swatchClassName: "bg-rp-chart-3" },
  { stroke: "var(--rp-chart-4)", swatchClassName: "bg-rp-chart-4" },
  { stroke: "var(--rp-chart-5)", swatchClassName: "bg-rp-chart-5" },
  { stroke: "var(--rp-chart-6)", swatchClassName: "bg-rp-chart-6" },
  { stroke: "var(--rp-chart-7)", swatchClassName: "bg-rp-chart-7" },
  { stroke: "var(--rp-chart-8)", swatchClassName: "bg-rp-chart-8" },
  { stroke: "var(--rp-chart-9)", swatchClassName: "bg-rp-chart-9" },
  { stroke: "var(--rp-chart-10)", swatchClassName: "bg-rp-chart-10" },
];
const TOTAL_CHART_COLOR: ChartColorToken = {
  stroke: "var(--rp-chart-total)",
  swatchClassName: "bg-rp-chart-total",
};

async function fetchAllPages<T>(
  fetcher: (params: { page: number; pageSize: number }) => Promise<PaginatedResponse<T>>
): Promise<T[]> {
  const first = await fetcher({ page: 1, pageSize: PAGE_SIZE });
  const all = [...first.items];
  if (first.totalPages <= 1) return all;

  const remaining = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      fetcher({ page: i + 2, pageSize: PAGE_SIZE })
    )
  );
  remaining.forEach((page) => all.push(...page.items));
  return all;
}

function productKey(productType: ProductAggregateItem["productType"], productId: number): string {
  return `${productType}_${productId}`;
}

function getProductColor(
  productColorByKey: Map<string, ChartColorToken>,
  key: string,
  fallbackIndex: number
): ChartColorToken {
  return productColorByKey.get(key) ?? PRODUCT_CHART_COLORS[fallbackIndex % PRODUCT_CHART_COLORS.length];
}

function getLocalDateBucket(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period.split("T")[0] ?? period;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : period.split("T")[0] ?? period;
}

function formatPeriodWithTime(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatAxisDateBucket(bucket: string): string {
  const [, month, day] = bucket.split("-");
  return month && day ? `${month}/${day}` : bucket;
}

function formatLatestAxisLabel(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  const dateLabel = formatAxisDateBucket(getLocalDateBucket(period, timeZone));
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${dateLabel} ${timeLabel}`;
}

function getLatestByPeriod<T extends { period: string }>(items: T[]): T | null {
  if (items.length === 0) return null;
  const sorted = [...items].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  return sorted[sorted.length - 1] ?? null;
}

function getPreviousByPeriod<T extends { period: string }>(items: T[]): T | null {
  if (items.length < 2) return null;
  const sorted = [...items].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  return sorted[sorted.length - 2] ?? null;
}

function getLatestProductRows(items: ProductAggregateItem[]): ProductAggregateItem[] {
  const latest = new Map<string, ProductAggregateItem>();
  items.forEach((item) => {
    const key = productKey(item.productType, item.productId);
    const existing = latest.get(key);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      latest.set(key, item);
    }
  });
  return Array.from(latest.values()).sort((a, b) => b.totalAum - a.totalAum);
}

function getProductsForProviderPeriod(
  items: ProductAggregateItem[],
  providerPeriod: string
): ProductAggregateItem[] {
  return items.filter((item) => item.period === providerPeriod);
}

async function loadProductNames(): Promise<Map<string, string>> {
  const [baskets, portfolios] = await Promise.all([
    fetchAllPages<BasketListItem>((params) => basketsV3Service.getBaskets(params)),
    fetchAllPages<PortfolioListItem>((params) => portfoliosV3Service.getPortfolios(params)),
  ]);

  const names = new Map<string, string>();
  baskets.forEach((basket) => names.set(productKey("BASKET", basket.id), basket.title));
  portfolios.forEach((portfolio) => names.set(productKey("PORTFOLIO", portfolio.id), portfolio.title));
  return names;
}

function fallbackProductTitle(item: ProductAggregateItem): string {
  const label = item.productType === "BASKET" ? "Basket" : "Portfolio";
  return `${label} #${item.productId}`;
}

function formatSignedNumber(value: number): string {
  if (value === 0) return "0";
  const sign = value > 0 ? "+" : "-";
  return `${sign}${formatNumber(Math.abs(value), 0)}`;
}

function formatSignedCurrency(value: number): string {
  if (value === 0) return "$0";
  const sign = value > 0 ? "+" : "-";
  return `${sign}${formatCurrencyWithCommas(Math.abs(value), 0)}`;
}

function formatSharePointsChange(deltaPoints: number): string {
  if (deltaPoints === 0) return "0.0 pts";
  const sign = deltaPoints > 0 ? "+" : "-";
  return `${sign}${formatPercentage(Math.abs(deltaPoints), 1).replace("%", "")} pts`;
}

// Stat Tile

interface StatTileProps {
  icon: LucideIcon;
  title: string;
  value: string;
  change?: string | null;
  changeTone?: "positive" | "negative" | "neutral";
}

const StatTile: React.FC<StatTileProps> = ({ icon, title, value, change, changeTone = "neutral" }) => {
  const Icon = icon;
  const isPositive = changeTone === "positive";
  const isNegative = changeTone === "negative";
  const ChangeIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  return (
    <div className="bg-black rounded-lg border border-gray-800 p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg bg-gray-900">
          <Icon className="w-4 h-4 text-rp-amber" />
        </div>
        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">{title}</span>
      </div>
      <p className="text-2xl font-bold text-white font-mono">{value}</p>
      {change && (
        <div className="flex items-center gap-1.5 mt-2">
          <ChangeIcon
            className={`w-3.5 h-3.5 ${
              isPositive ? "text-green-500" : isNegative ? "text-red-500" : "text-gray-500"
            }`}
          />
          <span
            className={`text-xs font-mono font-semibold ${
              isPositive ? "text-green-500" : isNegative ? "text-red-500" : "text-gray-500"
            }`}
          >
            {change}
          </span>
        </div>
      )}
    </div>
  );
};

// AUM Growth Chart

interface AumChartProps {
  providerAggregates: ProviderAggregateItem[];
  productAggregates: ProductAggregateItem[];
  latestProductSnapshots: ProductAggregateItem[];
  productNames: Map<string, string>;
  selectedProductKey: string | null;
  onSelectProduct: (key: string | null) => void;
  timeZone: string;
  latestProviderPeriod: string | null;
  productColorByKey: Map<string, ChartColorToken>;
}

function buildProductsByBucket(
  items: ProductAggregateItem[],
  timeZone: string
): Map<string, Map<string, ProductAggregateItem>> {
  const productsByBucket = new Map<string, Map<string, ProductAggregateItem>>();
  items.forEach((item) => {
    const bucket = getLocalDateBucket(item.period, timeZone);
    const key = productKey(item.productType, item.productId);
    if (!productsByBucket.has(bucket)) {
      productsByBucket.set(bucket, new Map());
    }
    const products = productsByBucket.get(bucket)!;
    const existing = products.get(key);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      products.set(key, item);
    }
  });
  return productsByBucket;
}

function buildAumChartData(
  providerAggregates: ProviderAggregateItem[],
  productAggregates: ProductAggregateItem[],
  latestProductSnapshots: ProductAggregateItem[],
  productNames: Map<string, string>,
  timeZone: string,
  latestProviderPeriod: string | null,
  productColorByKey: Map<string, ChartColorToken>
): { chartData: ChartDataPoint[]; lines: LineConfig[]; xAxisTicks: XAxisTickConfig[] } {
  const sortedProvider = [...providerAggregates].sort(
    (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
  );

  if (sortedProvider.length === 0) return { chartData: [], lines: [], xAxisTicks: [] };

  const productKeys = new Set<string>();
  [...productAggregates, ...latestProductSnapshots].forEach((item) => {
    productKeys.add(productKey(item.productType, item.productId));
  });

  const dailyProductsByBucket = buildProductsByBucket(productAggregates, timeZone);
  const latestProductsByBucket = buildProductsByBucket(latestProductSnapshots, timeZone);

  const sortedKeys = Array.from(productKeys);
  const xAxisTicks: XAxisTickConfig[] = [];

  const chartData: ChartDataPoint[] = sortedProvider.map((prov) => {
    const dateOnly = getLocalDateBucket(prov.period, timeZone);
    const isLatestPoint = latestProviderPeriod === prov.period;
    const point: ChartDataPoint = {
      date: dateOnly,
      fullDate: isLatestPoint ? prov.period : dateOnly,
      total_aum: prov.totalAum,
    };
    const periodProducts = isLatestPoint
      ? latestProductsByBucket.get(dateOnly) ?? dailyProductsByBucket.get(dateOnly)
      : dailyProductsByBucket.get(dateOnly);
    sortedKeys.forEach((key) => {
      point[key] = periodProducts?.get(key)?.totalAum ?? null;
    });
    xAxisTicks.push({
      value: point.fullDate ?? point.date,
      label: isLatestPoint ? formatLatestAxisLabel(prov.period, timeZone) : formatAxisDateBucket(dateOnly),
    });
    return point;
  });

  const lines: LineConfig[] = [
    {
      dataKey: "total_aum",
      stroke: TOTAL_CHART_COLOR.stroke,
      strokeWidth: 2,
      label: "Total AUM",
      swatchClassName: TOTAL_CHART_COLOR.swatchClassName,
    },
    ...sortedKeys.map((key, idx) => {
      const color = getProductColor(productColorByKey, key, idx);
      return {
        dataKey: key,
        stroke: color.stroke,
        strokeWidth: 1.5,
        label: productNames.get(key) ?? key.replace("_", " #"),
        swatchClassName: color.swatchClassName,
      };
    }),
  ];

  return { chartData, lines, xAxisTicks };
}

const AumGrowthChart: React.FC<AumChartProps> = ({
  providerAggregates,
  productAggregates,
  latestProductSnapshots,
  productNames,
  selectedProductKey,
  onSelectProduct,
  timeZone,
  latestProviderPeriod,
  productColorByKey,
}) => {
  const { chartData, lines: allLines, xAxisTicks } = useMemo(
    () => buildAumChartData(
      providerAggregates,
      productAggregates,
      latestProductSnapshots,
      productNames,
      timeZone,
      latestProviderPeriod,
      productColorByKey
    ),
    [providerAggregates, productAggregates, latestProductSnapshots, productNames, timeZone, latestProviderPeriod, productColorByKey]
  );

  const lines = useMemo(() => {
    if (!selectedProductKey) return allLines;
    return allLines.filter(
      (line) => line.dataKey === selectedProductKey || line.dataKey === "total_aum"
    );
  }, [allLines, selectedProductKey]);

  const CHART_HEIGHT = 400;

  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: chartData,
    lines,
    isDarkMode: true,
    height: CHART_HEIGHT,
    margins: { top: 12, right: 72, bottom: 24, left: 12 },
    yAxisFormatter: (value: number) => {
      if (Math.abs(value) >= 1_000_000) return `$${formatNumber(value / 1_000_000, 1)}M`;
      if (Math.abs(value) >= 1_000) return `$${formatNumber(value / 1_000, 0)}K`;
      return `$${formatNumber(value, 0)}`;
    },
    tooltipFormatter: (value: number) => formatCurrencyWithCommas(value, 0),
    showLegend: false,
    renderingMode: "enhanced",
    xAxisTicks,
  });

  if (chartData.length === 0 || lines.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <BarChart2 className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No daily AUM data available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 text-xs text-gray-300">
        {allLines.map((line) => {
          const isSelected = !selectedProductKey || line.dataKey === selectedProductKey || line.dataKey === "total_aum";
          return (
            <button
              key={line.dataKey}
              onClick={() => {
                if (line.dataKey === "total_aum") {
                  onSelectProduct(null);
                } else {
                  onSelectProduct(selectedProductKey === line.dataKey ? null : line.dataKey);
                }
              }}
              className={`flex max-w-[220px] items-center gap-2 transition-opacity ${
                isSelected ? "opacity-100" : "opacity-30"
              }`}
            >
              <span
                className={`h-0.5 w-5 flex-shrink-0 rounded-full ${line.swatchClassName ?? ""}`}
              />
              <span className="truncate">{line.label}</span>
            </button>
          );
        })}
      </div>

      <div
        ref={containerRef}
        className="relative overflow-visible"
        style={{ width: "100%", height: CHART_HEIGHT }}
      >
        <svg ref={svgRef} className="block" />
        <div
          ref={tooltipRef}
          className="absolute hidden rounded-md border border-slate-700 bg-slate-950 px-3 py-2 shadow-lg pointer-events-none z-[1000]"
        />
      </div>
    </div>
  );
};

// Donut Chart

interface DonutChartProps {
  items: ProductAggregateItem[];
  productNames: Map<string, string>;
  selectedProductKey: string | null;
  onSelectProduct: (key: string | null) => void;
  productColorByKey: Map<string, ChartColorToken>;
}

const DonutChart: React.FC<DonutChartProps> = ({ items, productNames, selectedProductKey, onSelectProduct, productColorByKey }) => {
  const size = 200;
  const strokeWidth = 32;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const totalAum = items.reduce((sum, item) => sum + Math.max(0, item.totalAum), 0);
  let cumulative = 0;

  const slices = items
    .filter((item) => item.totalAum > 0)
    .map((item, index) => {
      const share = totalAum > 0 ? item.totalAum / totalAum : 0;
      const offset = cumulative;
      cumulative += share;
      const key = productKey(item.productType, item.productId);
      return {
        item,
        key,
        offset,
        share,
        title: productNames.get(key) ?? fallbackProductTitle(item),
        color: getProductColor(productColorByKey, key, index),
      };
    });

  return (
    <div className="flex flex-col items-center">
      {slices.length > 0 ? (
        <>
          <svg width={size} height={size} viewBox={`-6 -6 ${size + 12} ${size + 12}`} className="mb-4">
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="currentColor"
              className="text-gray-800"
              strokeWidth={strokeWidth}
            />
            {slices.map((slice) => {
              const isActive = !selectedProductKey || slice.key === selectedProductKey;
              return (
                <circle
                  key={slice.key}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={slice.color.stroke}
                  strokeWidth={isActive && selectedProductKey ? strokeWidth + 6 : strokeWidth}
                  strokeDasharray={`${slice.share * circumference} ${circumference}`}
                  strokeDashoffset={-slice.offset * circumference}
                  transform={`rotate(-90 ${center} ${center})`}
                  opacity={isActive ? 1 : 0.2}
                  className="transition-all duration-500 cursor-pointer"
                  onClick={() => onSelectProduct(selectedProductKey === slice.key ? null : slice.key)}
                />
              );
            })}
            <text
              x={center}
              y={center - 8}
              textAnchor="middle"
              className="fill-white text-2xl font-bold"
              style={{ fontSize: "22px", fontWeight: 700 }}
            >
              {selectedProductKey
                ? formatCurrencyWithCommas(
                    slices.find((s) => s.key === selectedProductKey)?.item.totalAum ?? 0, 0
                  )
                : formatCurrencyWithCommas(totalAum, 0)}
            </text>
            <text
              x={center}
              y={center + 14}
              textAnchor="middle"
              className="fill-gray-400"
              style={{ fontSize: "11px" }}
            >
              {selectedProductKey
                ? slices.find((s) => s.key === selectedProductKey)?.title ?? "AUM"
                : "Total AUM"}
            </text>
          </svg>

          <div className="w-full space-y-1.5 max-h-[200px] overflow-y-auto custom-scrollbar">
            {slices.map((slice) => {
              const isSelected = !selectedProductKey || slice.key === selectedProductKey;
              return (
                <button
                  key={slice.key}
                  onClick={() => onSelectProduct(selectedProductKey === slice.key ? null : slice.key)}
                  className={`flex w-full items-center justify-between gap-2 transition-opacity ${
                    isSelected ? "opacity-100" : "opacity-30"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${slice.color.swatchClassName}`}
                    />
                    <span className="text-xs text-gray-300 truncate">{slice.title}</span>
                  </div>
                  <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                    {formatPercentage(slice.share * 100, 1)}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <BarChart2 className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No AUM data to chart.</p>
        </div>
      )}
    </div>
  );
};

// Main Page

const ProviderDashboardPageV3: React.FC = () => {
  const [providerAggregates, setProviderAggregates] = useState<ProviderAggregateItem[]>([]);
  const [productAggregates, setProductAggregates] = useState<ProductAggregateItem[]>([]);
  const [latestProviderSnapshot, setLatestProviderSnapshot] = useState<ProviderAggregateItem | null>(null);
  const [latestProductSnapshots, setLatestProductSnapshots] = useState<ProductAggregateItem[]>([]);
  const [productNames, setProductNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductKey, setSelectedProductKey] = useState<string | null>(null);
  const loadIdRef = useRef(0);
  const dashboardTimezone = useMemo(() => getDashboardTimezone(), []);

  const load = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const [providerItems, productItems, latestProvider, latestProducts, names] = await Promise.all([
        aggregatesService.getProviderAggregates("daily"),
        aggregatesService.getProviderProductAggregates("daily"),
        aggregatesService.getProviderAggregates("latest"),
        aggregatesService.getProviderProductAggregates("latest"),
        loadProductNames().catch((err) => {
          console.error("Failed to load provider product names:", err);
          return new Map<string, string>();
        }),
      ]);

      if (loadIdRef.current !== loadId) return;
      setProviderAggregates(providerItems);
      setProductAggregates(productItems);
      setLatestProviderSnapshot(latestProvider[0] ?? null);
      setLatestProductSnapshots(latestProducts);
      setProductNames(names);
    } catch (err) {
      if (loadIdRef.current !== loadId) return;
      console.error("Failed to load provider dashboard aggregates:", err);
      setError("Failed to load dashboard metrics.");
    } finally {
      if (loadIdRef.current === loadId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mergedProviderAggregates = useMemo(() => {
    if (!latestProviderSnapshot) return providerAggregates;
    const latestPeriod = latestProviderSnapshot.period;
    const alreadyIncluded = providerAggregates.some((item) => item.period === latestPeriod);
    if (alreadyIncluded) return providerAggregates;
    return [
      ...providerAggregates,
      latestProviderSnapshot,
    ];
  }, [providerAggregates, latestProviderSnapshot]);

  const latestProvider = useMemo(
    () => latestProviderSnapshot ?? getLatestByPeriod(providerAggregates),
    [latestProviderSnapshot, providerAggregates]
  );
  const previousProvider = useMemo(
    () => getPreviousByPeriod(mergedProviderAggregates),
    [mergedProviderAggregates]
  );
  const currentLatestProductSnapshots = useMemo(
    () => latestProviderSnapshot
      ? getProductsForProviderPeriod(latestProductSnapshots, latestProviderSnapshot.period)
      : latestProductSnapshots,
    [latestProductSnapshots, latestProviderSnapshot]
  );
  const latestProducts = useMemo(() => {
    if (latestProviderSnapshot) {
      return [...currentLatestProductSnapshots].sort((a, b) => b.totalAum - a.totalAum);
    }
    if (latestProductSnapshots.length > 0) {
      return [...latestProductSnapshots].sort((a, b) => b.totalAum - a.totalAum);
    }
    return getLatestProductRows(productAggregates);
  }, [currentLatestProductSnapshots, latestProviderSnapshot, latestProductSnapshots, productAggregates]);
  const productColorByKey = useMemo(() => {
    const colorByKey = new Map<string, ChartColorToken>();
    const assignColor = (key: string) => {
      if (!colorByKey.has(key)) {
        colorByKey.set(key, PRODUCT_CHART_COLORS[colorByKey.size % PRODUCT_CHART_COLORS.length]);
      }
    };

    latestProducts.forEach((item) => assignColor(productKey(item.productType, item.productId)));
    productAggregates.forEach((item) => assignColor(productKey(item.productType, item.productId)));
    latestProductSnapshots.forEach((item) => assignColor(productKey(item.productType, item.productId)));
    return colorByKey;
  }, [latestProducts, productAggregates, latestProductSnapshots]);

  const userChange = latestProvider && previousProvider
    ? latestProvider.numOfUsers - previousProvider.numOfUsers
    : null;
  const aumChange = latestProvider && previousProvider
    ? latestProvider.totalAum - previousProvider.totalAum
    : null;
  const shareChange =
    latestProvider?.providerShare != null && previousProvider?.providerShare != null
      ? (latestProvider.providerShare - previousProvider.providerShare) * 100
      : null;

  return (
    <div className="flex flex-col h-full bg-black transition-colors duration-300">
      {/* Header */}
      <header className="sticky top-0 z-20 w-full border-b border-gray-800 bg-black/80 backdrop-blur-md px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border border-rp-amber text-rp-amber">Beta</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-300 border border-gray-700 hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 lg:p-8">
        {/* Error */}
        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6 flex items-center justify-between gap-4">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={load}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-800 text-red-400 hover:bg-red-900/20 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && providerAggregates.length === 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-black rounded-lg p-5 border border-gray-800 animate-pulse"
                >
                  <div className="h-3 bg-gray-800 rounded w-1/3 mb-4" />
                  <div className="h-7 bg-gray-800 rounded w-1/2" />
                </div>
              ))}
            </div>
            <div className="bg-black rounded-lg border border-gray-800 animate-pulse" style={{ height: 460 }} />
          </div>
        ) : (
          <>
            {/* Stat Tiles */}
            {latestProvider && (
              <p className="text-sm text-gray-300 mb-3">
                Last updated {formatPeriodWithTime(latestProvider.period, dashboardTimezone)}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatTile
                icon={DollarSign}
                title="Total AUM"
                value={latestProvider ? formatCurrencyWithCommas(latestProvider.totalAum, 0) : "--"}
                change={aumChange === null ? null : formatSignedCurrency(aumChange)}
                changeTone={aumChange === null ? "neutral" : aumChange > 0 ? "positive" : aumChange < 0 ? "negative" : "neutral"}
              />
              <StatTile
                icon={PieChart}
                title="Provider Share"
                value={
                  latestProvider?.providerShare != null
                    ? formatPercentage(latestProvider.providerShare * 100, 1)
                    : "--"
                }
                change={shareChange === null ? null : formatSharePointsChange(shareChange)}
                changeTone={shareChange === null ? "neutral" : shareChange > 0 ? "positive" : shareChange < 0 ? "negative" : "neutral"}
              />
              <StatTile
                icon={Users}
                title="Unique Users"
                value={latestProvider ? formatNumber(latestProvider.numOfUsers, 0) : "--"}
                change={userChange === null ? null : formatSignedNumber(userChange)}
                changeTone={userChange === null ? "neutral" : userChange > 0 ? "positive" : userChange < 0 ? "negative" : "neutral"}
              />
              <StatTile
                icon={Package}
                title="Active Products"
                value={formatNumber(latestProducts.length, 0)}
              />
            </div>

            {/* Chart + Donut Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
              {/* AUM Growth Chart */}
              <div className="lg:col-span-2 bg-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                  <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">
                    AUM Growth
                  </span>
                  <span className="text-[10px] text-gray-600 uppercase tracking-wider font-medium">
                    Daily + Latest
                  </span>
                </div>
                <div className="p-2">
                  <AumGrowthChart
                    providerAggregates={mergedProviderAggregates}
                    productAggregates={productAggregates}
                    latestProductSnapshots={currentLatestProductSnapshots}
                    productNames={productNames}
                    selectedProductKey={selectedProductKey}
                    onSelectProduct={setSelectedProductKey}
                    timeZone={dashboardTimezone}
                    latestProviderPeriod={latestProviderSnapshot?.period ?? null}
                    productColorByKey={productColorByKey}
                  />
                </div>
              </div>

              {/* Donut Card */}
              <div className="bg-rp-surface rounded-lg border border-rp-separator overflow-hidden">
                <div className="flex items-center justify-between px-6 pt-4">
                  <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">
                    AUM Allocation
                  </span>
                  <span className="text-xs font-mono text-gray-600">
                    {latestProducts.length} products
                  </span>
                </div>
                <div className="p-6 pt-4">
                  <DonutChart
                    items={latestProducts}
                    productNames={productNames}
                    selectedProductKey={selectedProductKey}
                    onSelectProduct={setSelectedProductKey}
                    productColorByKey={productColorByKey}
                  />
                </div>
              </div>
            </div>

            {/* Product Breakdown Table */}
            <div className="bg-rp-surface rounded-lg border border-rp-separator overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">
                  Product Breakdown
                </span>
                <span className="text-[10px] text-gray-600 uppercase tracking-wider font-medium">
                  Latest snapshot
                </span>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-[11px] text-gray-500 uppercase tracking-wider text-left px-4 py-3">Product</th>
                      <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3">Type</th>
                      <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3">Users</th>
                      <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3">AUM</th>
                      <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestProducts.length > 0 ? (
                      latestProducts.map((item, index) => {
                        const key = productKey(item.productType, item.productId);
                        const name = productNames.get(key) ?? fallbackProductTitle(item);
                        const totalAum = latestProducts.reduce((sum, p) => sum + Math.max(0, p.totalAum), 0);
                        const share = totalAum > 0 ? (item.totalAum / totalAum) * 100 : 0;

                        return (
                          <tr
                            key={key}
                            onClick={() => setSelectedProductKey(selectedProductKey === key ? null : key)}
                            className={`border-b border-gray-800 last:border-b-0 hover:bg-white/[0.02] transition-all cursor-pointer ${
                              selectedProductKey && selectedProductKey !== key ? "opacity-30" : "opacity-100"
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <span
                                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                    getProductColor(productColorByKey, key, index).swatchClassName
                                  }`}
                                />
                                <span className="text-sm font-medium text-white truncate">{name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-xs font-medium text-gray-400 bg-gray-800/60 px-2 py-0.5 rounded">
                                {item.productType === "BASKET" ? "Basket" : "Portfolio"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm text-white">
                              {formatNumber(item.numOfUsers, 0)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm font-semibold text-white">
                              {formatCurrencyWithCommas(item.totalAum, 0)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm text-gray-400">
                              {formatPercentage(share, 1)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                          No product data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {latestProducts.length > 0 && (
                    <tfoot>
                      <tr
                        className="border-t border-gray-700 hover:bg-white/[0.02] transition-colors cursor-pointer"
                        onClick={() => setSelectedProductKey(null)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${TOTAL_CHART_COLOR.swatchClassName}`}
                            />
                            <span className="text-sm font-semibold text-gray-300">Total</span>
                          </div>
                        </td>
                        <td />
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-sm font-semibold text-white">
                          {formatNumber(
                            latestProvider?.numOfUsers ?? latestProducts.reduce((sum, p) => sum + p.numOfUsers, 0),
                            0
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-sm font-bold text-white">
                          {formatCurrencyWithCommas(latestProducts.reduce((sum, p) => sum + p.totalAum, 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-sm text-gray-400">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                    {latestProducts.length} Products
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">AUM</span>
                </div>
                {latestProducts.length > 0 ? (
                  latestProducts.map((item, index) => {
                    const key = productKey(item.productType, item.productId);
                    const name = productNames.get(key) ?? fallbackProductTitle(item);
                    const totalAum = latestProducts.reduce((sum, p) => sum + Math.max(0, p.totalAum), 0);
                    const share = totalAum > 0 ? (item.totalAum / totalAum) * 100 : 0;

                    return (
                      <div
                        key={key}
                        onClick={() => setSelectedProductKey(selectedProductKey === key ? null : key)}
                        className={`flex items-center justify-between px-4 py-3 border-b border-gray-800 last:border-b-0 cursor-pointer transition-opacity ${
                          selectedProductKey && selectedProductKey !== key ? "opacity-30" : "opacity-100"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              getProductColor(productColorByKey, key, index).swatchClassName
                            }`}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{name}</p>
                            <p className="text-[10px] text-gray-500 uppercase">
                              {item.productType === "BASKET" ? "Basket" : "Portfolio"} &middot; {formatNumber(item.numOfUsers, 0)} users
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-sm font-mono font-semibold text-white">
                            {formatCurrencyWithCommas(item.totalAum, 0)}
                          </p>
                          <p className="text-[10px] font-mono text-gray-500">
                            {formatPercentage(share, 1)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    No product data available.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ProviderDashboardPageV3;
